import React, { useState } from 'react';

import { Controller, useForm } from 'react-hook-form';
import { getObjectSumValues } from 'utilitys/helpers/general';

import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import useEventWithDimensions from 'hooks/useEventWithDimensions';

import { useBattleCardsStore } from 'store/battlecards.store';

import { useMutationBulkUser } from 'api/mutations/bulkUser.mutation';
import { useMutationSaveFilterBulkAssignToUser } from 'api/mutations/saveFilterBulkAssignToUser.mutation';
import { ICountersDataResponse } from 'api/queries/battleCards.query';

import { AssigneeSelect } from 'components/common/AssigneeSelect/AssigneeSelect';

import { PinboardWidgetPills } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetPills/PinboardWidgetPills';
import { executionConfig } from 'components/Dashboards/Project/ProjectWrapper/executionConfig';
import { Button } from 'components/DesignSystem/Button';
import { Modal } from 'components/DesignSystem/Modal';
import { Switch } from 'components/DesignSystem/Switch';
import { ConfirmSaveModal, CustomizedFormErrorMessage } from 'components/common';

import classes from '../bulkAssing.module.scss';

interface IBulkAssignUsersProps {
    assigneeValue?: ISelectOption<string>;
    isApplyAutomatically: boolean;
}

interface Props {
    isShowModal: boolean;
    searchedFilterData: any;
    bulkAssigneeToUserUniqIdNotification: number;
    handleModalClose: () => void;
    setIsBulkLoading: React.Dispatch<React.SetStateAction<boolean>>;
    cardsCounters?: Record<keyof ICountersDataResponse, number>;
}

export const BulkAssignUsers = ({
    isShowModal,
    searchedFilterData,
    bulkAssigneeToUserUniqIdNotification,
    handleModalClose,
    setIsBulkLoading,
    cardsCounters,
}: Props) => {
    const { widgetFiltersData } = useBattleCardsStore();
    const { handleSnackBar } = useCustomSnackBar();
    const [confirmModal, setConfirmModal] = useState<boolean>(false);
    const { mutate } = useMutationBulkUser();
    const { mutate: mutateSaveFilterBulk } = useMutationSaveFilterBulkAssignToUser();
    const { sendEventWithDimensions } = useEventWithDimensions();
    const {
        watch,
        handleSubmit,
        control,
        formState: { errors },
        reset,
        setValue,
    } = useForm<IBulkAssignUsersProps>({
        defaultValues: {
            assigneeValue: undefined,
            isApplyAutomatically: false,
        },
    });
    const { assigneeValue, isApplyAutomatically } = watch();
    const handleSave = () => {
        if (assigneeValue?.value) {
            const params = {
                body: {
                    bulkAssigneeToUserUniqIdNotification,
                    bulkAssignee: assigneeValue.value,
                    ...searchedFilterData,
                },
                querystring: {
                    lastRunActivities: getObjectSumValues(cardsCounters),
                },
            };

            mutate(params.body, {
                onSuccess: () => {
                    setIsBulkLoading(true);
                    sendEventWithDimensions({
                        category: 'Battlecards',
                        action: 'Bulk Assign Users',
                        label: `filter: ${JSON.stringify(params.filter)}`,
                    });

                    isApplyAutomatically && mutateSaveFilterBulk(params);
                },
                onError: (error) => {
                    const message = 'Bulk request Failed';
                    DEFAULT_ERROR({ error, message, handleError: handleSnackBar });
                },
                onSettled: () => {
                    closeModal();
                },
            });
        }
    };
    const closeModal = () => {
        handleModalClose();
        reset();
    };

    return (
        <>
            <Modal
                open={isShowModal}
                onClose={closeModal}
                title={<>{executionConfig.battlecards.title} - Bulk Assign</>}
                actions={
                    <>
                        <Button size={'large'} variant={'secondary'} onClick={closeModal}>
                            Cancel
                        </Button>

                        <Button size={'large'} variant={'primary'} onClick={handleSubmit(() => setConfirmModal(true))}>
                            Assign
                        </Button>
                    </>
                }
            >
                <div className={classes.bulkAssingContent}>
                    <form onSubmit={handleSubmit(() => setConfirmModal(true))}>
                        {widgetFiltersData.length > 0 && (
                            <div className={classes.widgetFiltersWrapper}>
                                <div className={classes.widgetFiltersTitle}>Applied Filter:</div>
                                <div className={classes.widgetFiltersContent}>
                                    <PinboardWidgetPills key={'widgetFilters'} filters={widgetFiltersData} />
                                </div>
                            </div>
                        )}

                        <div className={classes.selectTitle}>
                            Assign{' '}
                            <span className={classes.bold}>{getObjectSumValues(cardsCounters).toLocaleString()}</span>{' '}
                            incomplete activities matching the current filter to the following user
                        </div>

                        <div className={classes.selectWrapper}>
                            <div className={classes.customWidth}>
                                <Controller
                                    render={({ field }) => <AssigneeSelect {...field} placeholder={'Select User...'} />}
                                    name={'assigneeValue'}
                                    control={control}
                                    rules={{
                                        validate: {
                                            required: (value) => Boolean(value) || 'Must select at least one user',
                                        },
                                    }}
                                />
                            </div>

                            {errors.assigneeValue && <CustomizedFormErrorMessage text={errors.assigneeValue.message} />}
                        </div>

                        {assigneeValue && (
                            <div className={classes.messageForTheUser}>
                                You will receive a notification once the bulk update is completed.
                            </div>
                        )}

                        <div className={classes.applyAutomatically}>
                            <Switch
                                data-testid={'switch-advanced'}
                                onChange={(event) => setValue('isApplyAutomatically', event.target.checked)}
                                checked={isApplyAutomatically}
                                label={'Apply automatically to newly uploaded files'}
                                labelPlacement={'start'}
                            />
                        </div>
                    </form>
                </div>
            </Modal>

            <ConfirmSaveModal
                isShowModal={confirmModal}
                onHideModal={() => setConfirmModal(false)}
                onConfirm={() => {
                    setConfirmModal(false);
                    handleSave();
                }}
                actionButtonLabel={'Yes'}
            >
                Are you sure you want to run this bulk assign?
                <br />
                Large number of assignments may take a while...
            </ConfirmSaveModal>
        </>
    );
};
